import React, { useEffect, useState } from "react";
import NumericInput from "react-numeric-input";
import { Offer } from "../../Types";
import Decimal from "decimal.js";
import {
  formatMinMaxAmount,
  handleAmountChange,
} from "../../../Offers/helpers";
import { t } from "../../../../../utils/i18n";

interface Props {
  offer: Offer;
  isFixedPrice: boolean;
  rangeMax: string;
  rangeMin: string;
  tradeType: string;
  hasError: boolean;
  margin: string;
  actualRate: number;
  offerTerms: string;
  maxFieldValidationMessage: string;
  minFieldValidationMessage: string;
  marketrate: number;
  canEditAmount: boolean;
  setOfferTerms: (value: string) => void;
  hasExternalRate: boolean;
  validationMessage: string;
  amount: string;
  offerTradeRate: string;
  setOfferTradeRate: (value: string) => void;
  availableBalance: number;
  setRangeMax: (value: string) => void;
  setRangeMin: (value: string) => void;
  setMargin: (value: string) => void;
  profanityErrorMessage: string;
  handleProfanityCheck: (value: string) => void;
  updateOffer: () => void;
  getFiatAmount: (value: string, value2: string) => string;
  updatelimitsValidation: (value: string) => void;
}

const Limit: React.FC<Props> = ({
  offer,
  rangeMax,
  rangeMin,
  tradeType,
  isFixedPrice,
  canEditAmount,
  margin,
  hasError,
  actualRate,
  offerTerms,
  availableBalance,
  hasExternalRate,
  validationMessage,
  marketrate,
  setRangeMax,
  setRangeMin,
  setMargin,
  setOfferTerms,
  updateOffer,
  getFiatAmount,
  amount,
  offerTradeRate,
  setOfferTradeRate,
  maxFieldValidationMessage,
  minFieldValidationMessage,
  updatelimitsValidation,
  profanityErrorMessage,
  handleProfanityCheck,
}) => {
  const verb = tradeType === "sell" ? t("p2p.common.verb_sell") : t("p2p.common.verb_buy");
  const verbPast = tradeType === "sell" ? t("p2p.common.verb_sold") : t("p2p.common.verb_bought");
  const cryptoCode = offer.cryptoCurrency?.code === "USDT" ? "USD" : offer.cryptoCurrency?.code;
  const [tradeRateDisplay, setTradeRateDisplay] = useState(offerTradeRate);
  const currentOfferRate = hasExternalRate
    ? offer.fiatCurrency?.externalRates[0]?.overrideRate
    : offer.fiatCurrency?.rateToUsd;
  const [rangeMinDisplay, setRangeMinDisplay] = useState(
    new Intl.NumberFormat("en-US", {
      style: "decimal",
      maximumFractionDigits: 2,
    }).format(parseFloat(rangeMin) || 0)
  );
  const [rangeMaxDisplay, setRangeMaxDisplay] = useState(
    new Intl.NumberFormat("en-US", {
      style: "decimal",
      maximumFractionDigits: 2,
    }).format(parseFloat(rangeMax) || 0)
  );

  const calculateMinTradeLimit = () => {
    if (!rangeMin) {
      return "0.00";
    }

    const marginTemp = parseFloat(margin).toFixed(2);
    const rangeMinTemp = parseFloat(rangeMin);
    const calculatedMargin = (rangeMinTemp * parseFloat(marginTemp)) / 100;
    const calculatedAmount = calculatedMargin + rangeMinTemp;
    return calculatedAmount.toFixed(2);
  };

  const calculateTraderRate = (newMargin, currActualRate) => {
    const marginVal = newMargin ? new Decimal(Number(newMargin)) : 0;
    const rate = new Decimal(currActualRate);

    const tempAmount = rate.mul(marginVal).div(new Decimal(100));
    let result = rate.plus(tempAmount);
    let roundedResult = Number(result).toFixed(2);

    if (
      Math.abs(Number(roundedResult) - Math.floor(Number(roundedResult))) ===
      0.99
    ) {
      let steppedResult = Number(roundedResult) + 0.01;
      return steppedResult.toFixed(2);
    }
    return roundedResult;
  };

  const calculateNewMargin = (newValue, currActualRate) => {
    const numericValue = parseFloat(newValue);

    if (isNaN(numericValue)) {
      setMargin("0");
      setTradeRateDisplay(newValue);
      return;
    }

    const rate = new Decimal(currActualRate);
    const result = new Decimal(numericValue);

    // Reverse the margin calculation
    const tempAmount = result.minus(rate);
    const marginVal = tempAmount.div(rate).mul(new Decimal(100));

    setMargin(marginVal.toString());
  };
  useEffect(() => {
    setOfferTradeRate(tradeRateDisplay);
  }, [tradeRateDisplay]);

  return (
    <div className="editoffer-limits-cont">
      <div className="bg-white">
        <div>
          <div>
            <div>
              <div className="editoffer-tradelimit-title">
                <h3
                  className={`buy-section_title ${
                    tradeType === "sell" ? "sell-section_title " : ""
                  }`}
                >
                  <p className="createoffers-indexnumber"> 3 </p> {t("p2p.edit_limit.title")}
                </h3>
              </div>
              <div className="mt-4">
                <form className="limit-form createoffer-cont-boxshadow">
                  <p>
                    <i className="fas fa-info-circle"></i> {t("p2p.offer_shared.limit_info", { past: verbPast, verb })}{" "}
                    <b>
                      {t("p2p.edit_limit.info_limit", {
                        currency: offer.fiatCurrency?.code,
                        amount: formatMinMaxAmount(
                          getFiatAmount(marketrate.toString(), amount)
                        ),
                      })}
                    </b>
                  </p>
                  <div className="row">
                    <div
                      className="col-md-6"
                      title={t("p2p.offer_shared.min_tip", { verb, past: verbPast, amount })}
                    >
                      <label className="font-weight-bold pt-2">
                        <i className="fas fa-question-circle"></i> {t("p2p.offer_shared.minimum")}
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="createoffer-input"
                          placeholder="0.00"
                          autoFocus={true}
                          disabled={!canEditAmount}
                          value={rangeMinDisplay}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^0-9.]/g, "")
                              .replace(/(\..*)\./g, "$1");
                            handleAmountChange(
                              e,
                              setRangeMin,
                              setRangeMinDisplay
                            );
                          }}
                          onBlur={() => updatelimitsValidation("minfield")}
                        />
                        <div className="createoffer-currency-code-tag">
                          <p>{offer.fiatCurrency?.code}</p>
                        </div>
                      </div>
                      {!canEditAmount && (
                        <p style={{ color: "red" }}>
                          <i className="fas fa-exclamation-triangle"></i>
                          {t("p2p.edit_limit.locked")}
                        </p>
                      )}
                      {hasError && minFieldValidationMessage.length > 0 ? (
                        <div className="createoffer-error-alert">
                          {minFieldValidationMessage}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                    <div
                      className="col-md-6"
                      title={t("p2p.offer_shared.max_tip", { verb, past: verbPast, amount })}
                    >
                      <label className="font-weight-bold pt-2">
                        <i className="fas fa-question-circle"></i> {t("p2p.offer_shared.maximum")}
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="createoffer-input"
                          placeholder="0.00"
                          disabled={!canEditAmount}
                          autoFocus={true}
                          value={rangeMaxDisplay}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^0-9.]/g, "")
                              .replace(/(\..*)\./g, "$1");
                            handleAmountChange(
                              e,
                              setRangeMax,
                              setRangeMaxDisplay
                            );
                          }}
                          onBlur={() => updatelimitsValidation("maxfield")}
                        />
                        <div className="createoffer-currency-code-tag">
                          <p>{offer.fiatCurrency?.code}</p>
                        </div>
                      </div>
                      {hasError && maxFieldValidationMessage.length > 0 ? (
                        <div className="createoffer-error-alert">
                          {maxFieldValidationMessage}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <div className="editoffer-terms">
              <div className="editoffer-pricing-title">
                <label
                  className={`buy-section_title ${
                    tradeType === "sell" ? "sell-section_title " : ""
                  }`}
                >
                  <p className="createoffers-indexnumber"> 4 </p> {t("p2p.edit_limit.terms_title")}
                </label>
              </div>
              <div className="limit-form createoffer-cont-boxshadow">
                <div className="mt-2 mb-2 mr-sm-2">
                  <textarea
                    className="form-control bg-white editoffer-terms-text"
                    id="exampleInputPassword1"
                    placeholder={t("p2p.common.terms_placeholder")}
                    onChange={(e) => setOfferTerms(e.target.value)}
                    onBlur={() => handleProfanityCheck}
                    defaultValue={offerTerms}
                  ></textarea>
                </div>
                {hasError && profanityErrorMessage.length > 0 ? (
                  <div className="createoffer-error-alert">
                    {profanityErrorMessage}
                  </div>
                ) : (
                  <></>
                )}
                <p id="emailHelp" className="form-text text-muted">
                  {t("p2p.common.terms_help")}
                </p>
              </div>
            </div>

            <div>
              <div className="editoffer-pricing-title">
                <p
                  className={`buy-section_title ${
                    tradeType === "sell" ? "sell-section_title " : ""
                  }`}
                >
                  <p className="createoffers-indexnumber"> 5 </p> {t("p2p.edit_limit.price_title")}
                </p>
              </div>
              <div
                className={`createoffer-payment-method-wrapper ${
                  tradeType === "sell"
                    ? "createoffer-payment-method-wrapper-sell"
                    : ""
                }`}
              >
                <div>
                  <div className="text-muted font-1"></div>
                  <form>
                    <div className="mt-2 mb-5">
                      <p>
                        <i className="fas fa-info-circle"></i>{" "}
                        <strong>{t("p2p.offer_shared.convenient")}</strong>
                      </p>
                    </div>
                    <div
                      className="_input-field-card createoffer_input-field-card"
                      title={t("p2p.offer_shared.price_tip", { verb })}
                    >
                      <p className="createoffer-margin">
                        <b>{t("p2p.offer_shared.method1")}</b>
                      </p>
                      <label className="createoffer-sublabels">
                        {t("p2p.offer_shared.method1_q", { verb, currency: offer.fiatCurrency?.code })}
                      </label>
                      <div className="input-group mb-1">
                        <input
                          type="text"
                          value={tradeRateDisplay || " "}
                          className="createoffer-input"
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setTradeRateDisplay(newValue);
                            calculateNewMargin(newValue, actualRate);
                          }}
                        />
                        <div className="createoffer-currency-code-tag">
                          <p>{offer.fiatCurrency?.code}</p>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div
                        className="col-md-12 mt-4"
                        title={t("p2p.offer_shared.margin_tip")}
                      >
                        <p className="createoffer-margin">
                          <b>{t("p2p.offer_shared.method2")}</b>
                          <p>
                            <i className="fas fa-question-circle"></i> {t("p2p.offer_shared.learn")}{" "}
                            <strong>
                              <a
                                href={
                                  tradeType === "buy"
                                    ? "https://www.youtube.com/watch?v=QOKGxcnJVg4"
                                    : "https://www.youtube.com/watch?v=RH6OPldYDmU"
                                }
                                target="_blank"
                              >
                                {t("p2p.offer_shared.click_here")}
                              </a>
                            </strong>
                          </p>
                        </p>
                        <label className="createoffer-sublabels">
                          {t("p2p.offer_shared.method2_q", { verb, currency: offer.fiatCurrency?.code })}
                        </label>
                        <div className="input-group mt-2 mb-2 mr-sm-2">
                          <NumericInput
                            min={-10}
                            defaultValue={5}
                            value={margin}
                            step={0.01}
                            disabled={!canEditAmount}
                            mobile
                            className="form-control offer-margin-ranger pt-3 pb-2 createoffer-input"
                            onChange={(value) => {
                              setMargin(value);
                              setTradeRateDisplay(
                                calculateTraderRate(value, actualRate)
                              );
                            }}
                          />
                          <div className="input-group-prepend">
                            <div className="input-group-text">
                              <span className="font-weight-bold">%</span>
                            </div>
                          </div>
                        </div>
                        {!canEditAmount && (
                          <p>
                            {t("p2p.edit_limit.margin_locked")}
                          </p>
                        )}
                        <div className="pb-2">
                          <p>
                            {t("p2p.offer_shared.margin_hint")}
                          </p>
                          <p>
                            <i className="fas fa-bullseye"></i>{" "}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t("p2p.edit_limit.market_price_html", {
                                  crypto: cryptoCode,
                                  rate: actualRate,
                                  currency: offer.fiatCurrency?.code,
                                }),
                              }}
                            />
                          </p>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t("p2p.edit_limit.profit_html", { margin }),
                            }}
                          />
                          <div
                            dangerouslySetInnerHTML={{
                              __html: t("p2p.edit_limit.worth_html", {
                                min: rangeMin ? parseFloat(rangeMin).toFixed(2) : "0.00",
                                currency: offer.fiatCurrency?.code,
                                action: tradeType === "sell" ? t("p2p.common.receive") : t("p2p.common.pay"),
                                amount: calculateMinTradeLimit(),
                                crypto: cryptoCode,
                              }),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </form>

                  <br />
                  <div className="createoffer-yourrate">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("p2p.offer_shared.your_rate_html", {
                          rate:
                            formatMinMaxAmount(tradeRateDisplay) ||
                            formatMinMaxAmount(calculateTraderRate(margin, actualRate)),
                          currency: offer?.fiatCurrency.code,
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Limit;
